import { DeleteOutlined, DownloadOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Flex,
  Space,
  Switch,
  Typography,
  Upload,
} from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { updateTask } from '@/api/interviews';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUrlState } from '@/hooks/useUrlState';
import Card from '@/UI/Card/Card';
import { LabelTitle, SectionHeading } from '@/UI/typography';
import { mergeSearchParams } from '@/utils/routes';

import RescheduleDate from './RescheduleDate';

import type { InterviewRoom, InterviewTask } from '@/types/interview';
import type { RcFile } from 'antd/lib/upload';
import type React from 'react';

type KanbanTask = InterviewTask & {
  description?: string;
  decision_maker?: string;
  title?: string;
  document?: string;
  positive_decision?: boolean | null;
};

interface TaskProps {
  data: KanbanTask;
  name?: string;
  interview: InterviewRoom;
  handleComplete: (taskId: string) => void;
}

const TaskItem = ({ data, handleComplete, name, interview }: TaskProps) => {
  const [t] = useTranslation();
  const { user } = useCurrentUser();
  const isClient = user?.id === interview.client.id;
  const isDecisionMaker = !isClient && data.email === user?.email;
  const isAllowed = isClient || isDecisionMaker;
  const isPlaning = data.is_planning_task;
  const isDateProposed = data.is_planning_task && data.time_from;
  const isDecision = data.is_decision_task;
  const isUpload = data.is_upload_document_task;
  const [completed, setCompleted] = useState<boolean>(
    data.status === 'completed',
  );
  const { history } = useUrlState();
  const [file, setFile] = useState(data.document);
  const [originalFile, setOriginalFile] = useState<RcFile | null>(null);
  const [isPositive, setIsPositive] = useState<boolean | null>(
    data.positive_decision ?? null,
  );
  const updateTaskItem = async (info: Record<string, unknown> | FormData) => {
    return updateTask(interview.id, data.id, info);
  };
  const handleChange = () => {
    if (!completed && isAllowed) {
      if (isDecision && typeof isPositive == 'boolean') {
        void updateTaskItem({ positive_decision: isPositive }).then(() => {
          handleComplete(data.id);
          setCompleted(true);
        });
      } else if (isUpload) {
        if (!originalFile) return;
        const formData = new FormData();
        formData.append('document', originalFile);
        void updateTaskItem(formData).then((res) => {
          const taskData = res as { document?: string };
          setFile(taskData.document);
          setOriginalFile(null);
          handleComplete(data.id);
          setCompleted(true);
        });
      } else {
        handleComplete(data.id);
        setCompleted(true);
      }
    }
  };
  const onDecisionSwitchChange = (checked: boolean) => {
    if (isAllowed && !completed) {
      setIsPositive(checked);
    }
  };
  const handleFeedback = () => {
    mergeSearchParams(history, { feedback: interview.id }, ['tasks']);
  };
  const handleUploadFile = (file: RcFile): boolean => {
    setOriginalFile(file);
    return true;
  };
  const handleFileDelete = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setOriginalFile(null);
  };
  const leftOnlyFeedback = data.is_feedback_task;
  return (
    <Card
      variant="outlined"
      size="small"
      style={{
        backgroundColor: completed
          ? 'var(--ant-color-lozenge)'
          : 'var(--ant-color-bg-container)',
      }}
    >
      <Checkbox
        checked={completed}
        onChange={data.is_feedback_task ? handleFeedback : handleChange}
        disabled={
          (isDecision && typeof isPositive != 'boolean') ||
          (isUpload && !originalFile && !file) ||
          !isAllowed
        }
      />
      <Flex vertical>
        <Flex
          justify="space-between"
          align="center"
          onClick={() => {
            if (leftOnlyFeedback) {
              handleFeedback();
            }
          }}
          style={{ cursor: leftOnlyFeedback ? 'pointer' : 'unset' }}
        >
          <SectionHeading>
            {data.is_decision_task ? data.description : data.task_type}
          </SectionHeading>
          {isPlaning && isDateProposed && isAllowed && (
            <RescheduleDate interview={interview} history={history} />
          )}
        </Flex>
        <Typography.Text type="secondary">
          {data.decision_maker ? data.decision_maker : name}, {data.title}
          {isAllowed && ', ' + data.email}
        </Typography.Text>
        {isDecision && isAllowed && (
          <Space align="center">
            <Switch
              checked={isPositive === true}
              disabled={!isAllowed}
              onChange={onDecisionSwitchChange}
            />
            <Typography.Text type="secondary">
              {isPositive === null
                ? t('SELECT_DECISION')
                : isPositive
                  ? t('YES')
                  : t('NO')}
            </Typography.Text>
          </Space>
        )}
        {isUpload &&
          isAllowed &&
          (file ? (
            <Typography.Link href={file} target="_blank" rel="noreferrer">
              <DownloadOutlined />
              {t('DOWNLOAD_FILE')}
            </Typography.Link>
          ) : isAllowed ? (
            <Upload
              showUploadList={false}
              beforeUpload={handleUploadFile}
              customRequest={() => void 0}
              disabled={!isAllowed}
            >
              <Flex align="center">
                <LabelTitle>{originalFile?.name}</LabelTitle>
                <Flex align="center">
                  <Button disabled={!isAllowed}>{t('CHOOSE_FILE')}</Button>
                  {originalFile && (
                    <Button
                      variant="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleFileDelete}
                      aria-label={t('DELETE')}
                    />
                  )}
                </Flex>
              </Flex>
            </Upload>
          ) : (
            <LabelTitle type="secondary">{t('NO_FILE')}</LabelTitle>
          ))}
      </Flex>
    </Card>
  );
};

export default TaskItem;
